import {Class} from '../utils'
import icons from '../resources/icons'
import Searcher from './Searcher'

/**
 *
 * @class Enables a client to add a custom searcher to Septima Search
 * @extends Septima.Search.Searcher
 * @constructs Septima.Search.ClientSearcher
 * @param {Object} options ClientSearcher expects these properties:
 * @param options.singular {string} The singular form of the object type returned by the searcher, eg "Photo"
 * @param options.plural {string} The plural form of the object type returned by the searcher, eg "Photos"
 * @param options.isAsync {boolean} Does provider.query() return a thenable or a direct object?
 * @param options.provider {Object|string} searcher The provider or the global name of the provider object (provider = window[provider]) Must implement query(query, limit)
 *
 * @example
 *  //Create a provider
 *       var dummyProvider = {
 *                "query": function(query, limit){
 *                    var results = [];
 *                    for (var i = 0;i<limit;i++){
 *                        results.push({
 *                            title: "title " + i,
 *                            description: "description " + i,
 *                            geometry: null
 *                        });
 *                    }
 *                    var result = {status: "ok", hits: 9999, results: results};
 *                    return result;
 *                },
 *                // The select method is optional. The controller will NOT emit SELECT in SS if defined 
 *                "select": function(result){
 *                  //Do something with result
 *                }
 *       };
 *
 * // Create a clientSearcher
 *        var dummySearcher = new Septima.Search.ClientSearcher({
 *            singular: "objekt",
 *            plural: "objekter",
 *            isAsync: false,
 *            provider: dummyProvider
 *       });
 *
 * // Add the searcher to septima search
 * controller.addSearcher("titleOfSearcher", dummySearcher);
 * // Or
 * connection.addSearcher("titleOfSearcher", dummySearcher);
 *
 *
 */

export default Class(Searcher, /** @lends Septima.Search.ClientSearcher# */
  {

    initialize: function (options) {
      //Options:
      //  {
      //      [source: "nameOfSource",]
      //      singular: "objekt",
      //      plural: "objekter",
      //      isAsync: false,
      //      provider: provider
      //  }
      if (typeof options.provider === 'string') {
        this.provider = window[options.provider];
      } else {
        this.provider = options.provider;
      }

      if (!options.source) {
        options.source = options.plural;
      }
      this.source = options.source;
      //  provider.query(query, limit) must return
      //  {
      //    status: "ok|error",
      //    hits: nnn,
      //    results: results
      //  }, where
      //  results is an array of
      //  {
      //    title: "title ",
      //    description: "description",
      //    geometry: null|geojsonobject
      //  } 
      this.isAsync = options.isAsync;
      this.singular = options.singular;
      this.plural = options.plural;
      if (options.iconURI) {
        this.iconURI = options.iconURI;
      } else {
        this.iconURI = icons.result.defaultIcon;
      }

      this.Searcher(options);
      this.registerType(this.source, this.singular);
    },

    fetchData: async function (query, caller) {
      if (this.isAsync) {
        try {
          let providerResult = await this.provider.query(query.queryString, query.limit + 1);
          caller.fetchSuccess(this.formatResult(query, providerResult));
        } catch (error) {
          caller.fetchError(this, error);
        }
      } else {
        setTimeout(() => {
          try {
            let providerResult = this.provider.query(query.queryString, query.limit + 1);
            caller.fetchSuccess(this.formatResult(query, providerResult));
          } catch (error) {
            caller.fetchError(this, error);
          }
        }, 100);
      }
    },

    formatResult: function (query, providerResultArg) {

      let providerResult = null;
      if (typeof providerResultArg === 'string') {
        providerResult = JSON.parse(providerResultArg);
      } else {
        providerResult = providerResultArg;
      }

      let results = null;
      let totalMatchCount = null;
      const queryResult = this.createQueryResult();
      if (Array.isArray(providerResult)) {
        //Old style, only results returned
        results = providerResult;
      } else {
        if (providerResult.status.toLowerCase() === "ok") {
          if (providerResult.hits) {
            totalMatchCount = providerResult.hits;
          }
          results = providerResult.results;
        } else {
          return queryResult;
        }
      }

      if (results !== null) {
        const count = results.length;
        if (count > 0) {
          const listStrategy = this.getListStrategy(query, count);
          if (listStrategy.show) {
            for (let i = 0; i < listStrategy.count; i++) {
              let hit = results[i];
              let displayname = hit.title;
              if (listStrategy.isMixed) {
                displayname += " (" + this.singular + ")";
              }
              let description = null;
              if (hit.description) {
                description = hit.description;
              }
              let geometry = null;
              if (hit.geometry) {
                geometry = hit.geometry;
              }
              let result = queryResult.addResult(this.source, this.singular, displayname, description, geometry, hit);
              if (typeof hit.id !== 'undefined') {
                result.id = hit.id;
              }
            }
          } else {
            let title = this.plural;
            if (totalMatchCount !== null) {
              title = title + " (" + totalMatchCount + ")";
            }
            queryResult.addNewQuery(this.source, this.singular, title, null, query.queryString, null, null);
          }
        }
      } else {
        throw "clientSearcher: Could not parse providerResult";
      }
      return queryResult;
    },

    onSelect: function (result) {
      if (typeof result.newquery === 'undefined') {
        if (typeof this.provider.select !== 'undefined') {
          try {
            return this.provider.select(result.data);
          } catch (e) {
            // catch-all
          }
        }
        this._onSelectCallback(result);
      }
    },

    resultFromHit: function (hit) {
      const queryResult = this.createQueryResult();
      let displayname = hit.title;
      let description = null;
      if (hit.description) {
        description = hit.description;
      }
      let geometry = null;
      if (hit.geometry) {
        geometry = hit.geometry;
      }
      let result = queryResult.addResult(this.source, this.singular, displayname, description, geometry, hit);
      if (typeof hit.id !== 'undefined') {
        result.id = hit.id;
      }
      return result;
    },

    get: async function (id) {
      if (typeof this.provider.get !== 'undefined') {
        if (this.isAsync) {
          let hit = await this.provider.get(id);
          return this.resultFromHit(hit);
        } else {
          let hit = this.provider.get(id);
          return this.resultFromHit(hit);
        }
      } else {
        throw new Error('Whoops! get(id) is not implemented in ' + this.singular + ' searcher');
      }
    },

    CLASS_NAME: 'Septima.Search.ClientSearcher'
  });
